/**
 * Server Time Service
 * Fetches server timestamps from the cloud function API
 * Provides caching and fallback mechanisms
 */

import httpClient from '../config/httpClient';

let cachedServerTime = null;
let cacheTimestamp = null;
const CACHE_DURATION = 1000; // 1 second cache

/**
 * Fetches current server timestamp from the API
 * @returns {Promise<Object>} Server time response
 */
export const getServerTimestamp = async () => {
  try {
    const now = Date.now();
    if (
      cachedServerTime &&
      cacheTimestamp &&
      now - cacheTimestamp < CACHE_DURATION
    ) {
      console.log('🕐 Using cached server time');
      return cachedServerTime;
    }

    console.log('🕐 Fetching fresh server time from API');

    const response = await httpClient.get('/getUtcDate');
    const serverTimeData = response.data;

    console.log('🕐 Server time fetched successfully', {
      timestamp: serverTimeData.timestamp,
      toUTCString: serverTimeData.toUTCString,
      toISOString: serverTimeData.toISOString,
    });

    // Cache the response
    cachedServerTime = serverTimeData;
    cacheTimestamp = now;

    return serverTimeData;
  } catch (error) {
    console.error(
      '🕐 Error fetching server time, falling back to system time:',
      error
    );

    // Fallback to system time with warning
    const fallbackTime = new Date();
    const fallbackResponse = {
      timestamp: fallbackTime.toISOString(),
      toUTCString: fallbackTime.toUTCString(),
      toISOString: fallbackTime.toISOString(),
      fallback: true,
      error: error.message,
    };

    console.warn('🕐 Using system time fallback', fallbackResponse);
    return fallbackResponse;
  }
};

/**
 * Gets server time as Date object
 * @returns {Promise<Date>} Server time as Date object
 */
export const getServerTime = async () => {
  const serverTimeData = await getServerTimestamp();
  return new Date(serverTimeData.timestamp);
};

/**
 * Gets server time in ISO string format
 * @returns {Promise<string>} Server time in ISO format
 */
export const getServerTimeISO = async () => {
  const serverTimeData = await getServerTimestamp();
  return serverTimeData.toISOString;
};

/**
 * Gets server time in UTC string format
 * @returns {Promise<string>} Server time in UTC format
 */
export const getServerTimeUTC = async () => {
  const serverTimeData = await getServerTimestamp();
  return serverTimeData.toUTCString;
};

export default {
  getServerTimestamp,
  getServerTime,
  getServerTimeISO,
  getServerTimeUTC,
};
